import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import { toast } from "sonner";
import {
  riderGrabJob,
  riderJobs,
  riderLogin,
  riderMe,
  riderSummary,
  riderUpdateJob,
  STATUS_COLORS,
  STATUS_LABELS,
} from "../lib/api";

const TOKEN_KEY = "rw_rider_token";
const STATUSES = ["pending", "picked_up", "washing", "out_for_delivery", "delivered"];

function StatusPill({ status }) {
  return (
    <span
      className="inline-block px-2.5 py-1 rounded-full text-xs font-semibold"
      style={{ background: STATUS_COLORS[status]?.bg || "#eee", color: "#1A1A24" }}
    >
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function JobCard({ order, mine, onGrab, onStatus, onPayment }) {
  const wa = order.phone ? `https://wa.me/${order.phone.replace(/\D/g, "")}` : null;
  const tel = order.phone ? `tel:${order.phone.replace(/\s+/g, "")}` : null;
  const mapsUrl = order.lat != null && order.lng != null
    ? `https://www.google.com/maps/dir/?api=1&destination=${order.lat},${order.lng}`
    : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
        `${order.address || ""}${order.sector ? `, ${order.sector}` : ""}, Gurugram`
      )}`;
  const hasPreciseLocation = order.lat != null && order.lng != null;
  return (
    <div className="rounded-2xl border border-rw-ink/10 bg-rw-bg2 p-5" data-testid={`rider-job-${order.order_code}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <div className="font-mono font-semibold text-lg">{order.order_code}</div>
          <div className="text-xs text-rw-ink2 mt-1">
            {order.pickup_date} · {order.pickup_slot}
          </div>
        </div>
        <StatusPill status={order.status} />
      </div>
      <div className="mt-3 text-sm">
        <div className="font-semibold">{order.customer_name}</div>
        <div className="text-rw-ink2">{order.address}{order.sector ? `, ${order.sector}` : ""}</div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <a
          href={mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={`text-xs rounded-full px-3 py-1.5 font-semibold ${
            hasPreciseLocation ? "bg-rw-blue text-rw-ink" : "border border-rw-ink/20 hover:bg-rw-bg3"
          }`}
          data-testid={`rider-navigate-${order.order_code}`}
        >
          {hasPreciseLocation ? "📍 Navigate (exact pin)" : "🗺️ Open in Maps"}
        </a>
        {tel && (
          <a href={tel} className="text-xs rounded-full border border-rw-ink/20 px-3 py-1.5 hover:bg-rw-bg3" data-testid={`rider-call-${order.order_code}`}>
            📞 Call
          </a>
        )}
        {wa && (
          <a href={wa} target="_blank" rel="noopener noreferrer" className="text-xs rounded-full border border-rw-ink/20 px-3 py-1.5 hover:bg-rw-bg3" data-testid={`rider-whatsapp-${order.order_code}`}>
            💬 WhatsApp
          </a>
        )}
        <span className="ml-auto font-display text-lg">₹{order.total}</span>
      </div>

      {!mine ? (
        <button
          onClick={() => onGrab(order.order_code)}
          className="mt-4 btn-dark w-full rounded-full py-2 text-sm font-semibold"
          data-testid={`rider-grab-${order.order_code}`}
        >
          Grab this job →
        </button>
      ) : (
        <>
          <div className="mt-4 flex items-center gap-2">
            <select
              value={order.status}
              onChange={(e) => onStatus(order.order_code, e.target.value)}
              className="flex-1 rounded-md border border-rw-ink/20 bg-white px-3 py-2 text-sm rw-focus"
              data-testid={`rider-status-select-${order.order_code}`}
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>{STATUS_LABELS[s]}</option>
              ))}
            </select>
          </div>
          {order.payment_status !== "paid" && order.status === "delivered" && (
            <div className="mt-3 flex gap-2">
              <button
                onClick={() => onPayment(order.order_code, "cash")}
                className="flex-1 text-xs rounded-full bg-rw-mint px-3 py-2 font-semibold"
                data-testid={`rider-cash-${order.order_code}`}
              >
                Cash collected
              </button>
              <button
                onClick={() => onPayment(order.order_code, "upi")}
                className="flex-1 text-xs rounded-full bg-rw-sage/60 px-3 py-2 font-semibold"
                data-testid={`rider-upi-${order.order_code}`}
              >
                UPI received
              </button>
            </div>
          )}
          {order.payment_status === "paid" && (
            <div className="mt-3 text-xs text-rw-sage font-semibold">
              ✓ Paid via {order.payment_method || "unknown"}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function RiderPortal() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [rider, setRider] = useState(null);
  const [tab, setTab] = useState("mine");
  const [mine, setMine] = useState([]);
  const [pool, setPool] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ident, setIdent] = useState("");
  const [cred, setCred] = useState("");

  const load = async (t = token) => {
    if (!t) return;
    try {
      const [j, s, m] = await Promise.all([riderJobs(t), riderSummary(t), riderMe(t)]);
      setMine(j.mine || []);
      setPool(j.pool || []);
      setSummary(s);
      setRider(m.rider);
    } catch (e) {
      localStorage.removeItem(TOKEN_KEY);
      setToken("");
      setRider(null);
      if (t) toast.error("Session expired. Please sign in again.");
    }
  };

  useEffect(() => { if (token) load(token); }, [token]);

  const submitLogin = async (e) => {
    e.preventDefault();
    if (!ident || !cred) return;
    setLoading(true);
    try {
      const r = await riderLogin({ identifier: ident.trim(), credential: cred });
      localStorage.setItem(TOKEN_KEY, r.token);
      setToken(r.token);
      setRider(r.rider);
      toast.success(`Welcome, ${r.rider.name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setRider(null);
    setMine([]); setPool([]); setSummary(null);
  };

  const grab = async (code) => {
    try {
      await riderGrabJob(token, code);
      toast.success(`Grabbed ${code}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not grab job");
    }
  };

  const updateStatus = async (code, status) => {
    try {
      await riderUpdateJob(token, code, { status });
      toast.success(`Updated ${code} → ${STATUS_LABELS[status]}`);
      load();
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const markPayment = async (code, method) => {
    try {
      await riderUpdateJob(token, code, { payment_status: "paid", payment_method: method });
      toast.success(`Payment recorded (${method})`);
      load();
    } catch (e) {
      toast.error("Failed to record payment");
    }
  };

  if (!rider) {
    return (
      <div className="min-h-screen bg-rw-bg text-rw-ink flex flex-col" data-testid="rider-login-page">
        <Seo title="Rider Portal" description="RIZ N WASH runner sign-in." path="/rider" noindex={true} />
        <div className="max-w-md mx-auto px-6 md:px-10 pt-14 w-full">
          <div className="overline mb-3">Rider portal</div>
          <h1 className="font-display text-4xl md:text-5xl font-light tracking-tight leading-[1.05]">
            Hey runner.
          </h1>
          <p className="mt-3 text-rw-ink2">
            Sign in with your <strong>Rider ID + PIN</strong> or your <strong>phone + password</strong>.
          </p>

          <form onSubmit={submitLogin} className="mt-8 rounded-2xl border border-rw-ink/10 bg-rw-bg2 p-6 space-y-3" data-testid="rider-login-form">
            <label className="block">
              <span className="overline">Rider ID or phone</span>
              <input
                className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2.5 rw-focus"
                value={ident}
                onChange={(e) => setIdent(e.target.value)}
                placeholder="R12345 or 98xxx xxxxx"
                autoFocus
                data-testid="rider-identifier"
              />
            </label>
            <label className="block">
              <span className="overline">PIN or password</span>
              <input
                type="password"
                className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2.5 rw-focus"
                value={cred}
                onChange={(e) => setCred(e.target.value)}
                placeholder="••••"
                data-testid="rider-credential"
              />
            </label>
            <button
              type="submit"
              disabled={loading}
              className="btn-dark w-full rounded-full py-2.5 text-sm font-semibold disabled:opacity-40"
              data-testid="rider-login-btn"
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <p className="mt-6 text-sm text-rw-ink2 text-center">
            New rider? <Link to="/rider/register" className="text-rw-ink font-semibold hover:underline" data-testid="rider-link-register">Sign up here</Link>
          </p>
          <p className="mt-2 text-xs text-rw-ink3 text-center">
            Not a rider? <Link to="/" className="hover:text-rw-ink">Back to home</Link>
          </p>
        </div>
      </div>
    );
  }

  const jobs = tab === "mine" ? mine : pool;

  return (
    <div className="min-h-screen bg-rw-bg text-rw-ink" data-testid="rider-dashboard-page">
      <Seo title="Rider Dashboard" description="Manage your pickups & deliveries." path="/rider" noindex={true} />
      <header className="border-b border-rw-ink/10 bg-rw-bg2">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <div>
            <div className="overline">Rider</div>
            <div className="font-display text-xl">{rider.name} <span className="text-rw-ink3 font-mono text-sm ml-2">{rider.rider_code}</span></div>
          </div>
          <button onClick={logout} className="rounded-full border border-rw-ink/20 px-4 py-2 text-sm hover:bg-rw-bg3" data-testid="rider-logout-btn">
            Sign out
          </button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 md:px-10 pt-8">
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 border-t border-l border-rw-ink/10 bg-rw-bg2 rounded-2xl overflow-hidden mb-6" data-testid="rider-summary">
            {[
              ["Assigned today", summary.assigned],
              ["Active", summary.active],
              ["Delivered", summary.delivered],
              ["Cash", `₹${summary.cash_collected.toFixed(0)}`],
            ].map(([label, val], i) => (
              <div key={i} className="border-r border-b border-rw-ink/10 p-4">
                <div className="overline">{label}</div>
                <div className="font-display text-2xl md:text-3xl mt-1">{val}</div>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-wrap gap-2 border-b border-rw-ink/10 pb-3 mb-6">
          {[
            ["mine", `My jobs (${mine.length})`],
            ["pool", `Open pool (${pool.length})`],
          ].map(([id, label]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`px-4 py-2 text-sm font-semibold rounded-full ${
                tab === id ? "bg-rw-ink text-white" : "text-rw-ink2 hover:bg-rw-bg3"
              }`}
              data-testid={`rider-tab-${id}`}
            >
              {label}
            </button>
          ))}
          <button onClick={() => load()} className="ml-auto text-sm text-rw-ink2 hover:text-rw-ink" data-testid="rider-refresh-btn">
            ⟳ Refresh
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-4 pb-16" data-testid="rider-jobs-list">
          {jobs.length === 0 && (
            <div className="md:col-span-2 rounded-2xl border border-dashed border-rw-ink/20 p-10 text-center text-rw-ink2">
              {tab === "mine" ? "No active jobs assigned to you. Check the open pool!" : "No open jobs in your sectors right now."}
            </div>
          )}
          {jobs.map((o) => (
            <JobCard
              key={o.order_code}
              order={o}
              mine={tab === "mine"}
              onGrab={grab}
              onStatus={updateStatus}
              onPayment={markPayment}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
